import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { updatePersonalGoal, updatePersonalPage, updatePersonalPhotos, updateScreenName } from '../../../helpers/requests/fundraising-pages';
import {
  usePersonalFundraisingProgress,
  usePersonalPhoto,
  useDonationForm,
  usePersonalPageInfo,
  useParticipantAchievements,
  useScreenName,
  useBackgroundImageHeroes,
  useSurveyResponses,
  useEventInfo,
} from '../../../hooks';
import ProgressBar from '../../../components/progress-bar';
import DonationPills from '../../../components/donation-pills';
import PageTitle from '../../../components/page-title';
import MobileSocialShareModal from '../../../components/mobile-social-share-modal';
import TopList from '../../../components/top-list';
import ProfileImage from '../../../components/profile-image';
import SocialShare from '../../../components/social-share';
import { convertCentsToDollars } from '../../../helpers/base';
import EditGoalModal from '../../../components/edit-goal-modal';
import EditImageModal from '../../../components/edit-image-modal';
import EditBackgroundImageModal from '../../../components/edit-background-image-modal';
import EditRichTextModal from '../../../components/edit-rich-text-modal';
import EditTitleModal from '../../../components/edit-title-modal';
import { EVENT_TYPE } from '../../../constants/dom-events';
import ReactHtmlParser from 'react-html-parser';
import { updateSurveyResponses, updateSurveyResponsesForBackgrounds } from '../../../services/luminate';
import EditEventDetailsModal from '../../../components/edit-event-details-modal';

/** page constants */
const CONS_ID = luminateProperties.personalPage.consId;
const EVENT_ID = luminateProperties.personalPage.frId;
const EVENT_NAME = luminateProperties.eventName;
const EVENT_LINK = `${luminateProperties.secure}TR?fr_id=${EVENT_ID}&pg=entry`;
const FORM_ID = luminateProperties.personalPage.formId;
const IS_OWN_PAGE = luminateProperties.personalPage.ownPage;
const DONATION_LINK = `${luminateProperties.secure}Donation2?df_id=${FORM_ID}&${FORM_ID}.donation=form1&PROXY_ID=${CONS_ID}&PROXY_TYPE=20&FR_ID=${EVENT_ID}`;
const TEAM_ID = luminateProperties.personalPage.teamId;
const TEAM_NAME = luminateProperties.personalPage.teamName;
const TEAM_URL = `${luminateProperties.secure}TR?team_id=${TEAM_ID}&pg=team&fr_id=${EVENT_ID}`;
const USER_FIRST_NAME = luminateProperties.personalPage.participant.firstName;
const USER_FULL_NAME = `${luminateProperties.personalPage.participant.firstName} ${luminateProperties.personalPage.participant.lastName}`;

/** Event FRID Numbers */
const CHICAGO = process.env.HEROES_CHICAGO_FRIDS;
const NEWYORK = process.env.HEROES_NEWYORK_FRIDS;
const LONDON = process.env.HEROES_LONDON_FRIDS;
const BERLIN = process.env.HEROES_BERLIN_FRIDS;

let DEFAULT_BG_IMAGE;

const checkFrID = city => {
  if (city.includes(luminateProperties.frId)) {
    // console.log('yes found it');
    return luminateProperties.frId;
  }
};

switch (luminateProperties.frId) {
  // case CHICAGO.replace(/\D/g,''):
  case checkFrID(CHICAGO):
    DEFAULT_BG_IMAGE = '../p2p-ui-framework/dist/img/programs/heroes/backgrounds/chicago/background-1.jpg';
    break;
  // case NEWYORK.replace(/\D/g,''):
  case checkFrID(NEWYORK):
    DEFAULT_BG_IMAGE = '../p2p-ui-framework/dist/img/programs/heroes/backgrounds/new-york/background-1.jpg';
    break;
  // case LONDON.replace(/\D/g,''):
  case checkFrID(LONDON):
    DEFAULT_BG_IMAGE = '../p2p-ui-framework/dist/img/programs/heroes/backgrounds/london/background-1.jpg';
    break;
  // case checkFrID(BOSTON):
  //   DEFAULT_BG_IMAGE = '../p2p-ui-framework/dist/img/programs/heroes/backgrounds/boston/background-1.jpg';
  //   break;
  default:
    DEFAULT_BG_IMAGE = '../p2p-ui-framework/dist/img/programs/heroes/backgrounds/default/background-1.jpg';
}

/** BOUNDLESS API */
const BOUNDLESS_MOTION_DB = process.env.HEROES_BOUNDLESS_MOTION_DB;
const BOUNDLESS_USER = process.env.HEROES_BOUNDLESS_MOTION_USER;
const BOUNDLESS_PASS = process.env.HEROES_BOUNDLESS_MOTION_PASS;
const BOUNDLESS_MOTION_PREFIX = process.env.HEROES_BOUNDLESS_MOTION_PREFIX;
const username = BOUNDLESS_USER;
const password = BOUNDLESS_PASS;

const headers = new Headers();
headers.set('Authorization', 'Basic ' + Buffer.from(username + ':' + password).toString('base64'));

document.addEventListener(EVENT_TYPE.DOM_LOAD, () => {
  const $layout = document.querySelector('#appRoot');
  ReactDOM.render(<PersonalPage />, $layout);
});

const PersonalPage = () => {
  /** hooks */
  const personalProgress = usePersonalFundraisingProgress(EVENT_ID, CONS_ID);
  const personalPhoto = usePersonalPhoto(EVENT_ID, CONS_ID);
  const participantAchievements = useParticipantAchievements(EVENT_ID, CONS_ID);
  const donationForm = useDonationForm(FORM_ID, EVENT_ID);
  const personalPageInfo = usePersonalPageInfo(EVENT_ID, CONS_ID);
  const screenName = useScreenName(EVENT_ID, CONS_ID);
  const bgImageData = useBackgroundImageHeroes(EVENT_ID);
  const surveyResponses = useSurveyResponses(EVENT_ID, CONS_ID);

  const eventInfo = useEventInfo(EVENT_NAME, 'Heroes', '');

  /** state */
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [editGoalModalIsOpen, setEditGoalModalIsOpen] = useState(false);
  const [editTitleModalIsOpen, setEditTitleModalIsOpen] = useState(false);
  const [editPhotoModalIsOpen, setEditPhotoModalIsOpen] = useState(false);
  const [editBackgroundPhotoModalIsOpen, setEditBackgroundPhotoModalIsOpen] = useState(false);
  const [editRichTextModalIsOpen, setEditRichTextModalIsOpen] = useState(false);
  const [editEventModalIsOpen, setEditEventModalIsOpen] = useState(false);
  const [mobileSocialShareModalIsOpen, setMobileSocialShareModalIsOpen] = useState(false);

  const [donationAmount, setDonationAmount] = useState(false);
  const [donationLevelId, setDonationLevelId] = useState('0');
  const [isOtherAmount, setIsOtherAmount] = useState(false);
  const [selectedBackgroundImage, setSelectedBackgroundImage] = useState(DEFAULT_BG_IMAGE);

  /** value initializations */
  const donationLevels = donationForm.data.donationLevels.donationLevel;
  const selectedId = donationLevels.length > 0 ? donationLevels[1].level_id : 0;
  const otherAmount = document.getElementById('other_amount');

  //let hasEventDetails = false;

  /** Functions */
  const closeModals = () => {
    setEditGoalModalIsOpen(false);
    setEditTitleModalIsOpen(false);
    setEditPhotoModalIsOpen(false);
    setEditBackgroundPhotoModalIsOpen(false);
    setEditRichTextModalIsOpen(false);
    setEditEventModalIsOpen(false);
    setMobileSocialShareModalIsOpen(false);
    setErrorMessage(null);
  };

  const renderHeroImage = () => {
    const image = luminateProperties.personalPage.personalEvent.backgroundImage;
    const bgImg = image && image !== 'User Provided No Response' ? luminateProperties.personalPage.personalEvent.backgroundImage : DEFAULT_BG_IMAGE;

    return (
      <div className="hero has-page-covering" style={{ backgroundImage: `url('${bgImg}')` }}>
        <div className="has-text-right" data-container="background-image">
          {renderEditButton({
            text: 'Edit Photo',
            action: () => {
              setEditBackgroundPhotoModalIsOpen(true);
              setSelectedBackgroundImage(bgImg);
            },
          })}
        </div>
      </div>
    );
  };

  //Removed donation ask array by request
  /* const renderDonationPills = () => (
    <DonationPills
      donationLevels={donationLevels}
      clickHandler={handleSetDonationAmount}
      selectedId={selectedId}
      sectionTopText={`Support ${USER_FIRST_NAME} with a donation:`}
    />
  );

  const renderDonationButton = () => {
    if (donationLevels && !donationAmount) {
      // Set default selected
      donationLevels.map(level => {
        if (level.level_id === selectedId) {
          const level_id = level.level_id;
          const amount = level.amount.decimal.split('.')[0];
          handleSetDonationAmount({ level_id, amount });
        }
      });
    }

    return (
      <a
        href={
          isOtherAmount
            ? `${DONATION_LINK}&set.Value=${donationAmount * 100}&set.DonationLevel=${donationLevelId}`
            : `${DONATION_LINK}&set.DonationLevel=${donationLevelId}`
        }
        target="_blank"
        rel="noreferrer"
        className="button is-donate is-pulled-right donate"
      >
        Donate ${donationAmount ? donationAmount : '0'}
      </a>
    );
  }; */

  const renderDonationButton = () => (
    <a href={DONATION_LINK} target="_blank" rel="noreferrer" className="button is-donate is-pulled-right donate">
      Donate
    </a>
  );

  const renderProgressBar = () => {
    if (navigator.userAgent.includes('Firefox')) {
      const bodyTag = document.getElementsByTagName('body')[0];
      bodyTag.classList.add('is-firefox');
    }
    return (
      <ProgressBar
        goal={personalProgress.data.goal}
        progress={personalProgress.data.raised}
        showIndicator
        goalText="Goal:"
        raisedText="Amount Raised:"
        editButton={renderEditButton({
          text: 'Edit Goal',
          action: () => setEditGoalModalIsOpen(true),
          className: 'edit-goal',
        })}
      />
    );
  };

  const renderTopDonors = () => {
    const donors = document.getElementsByClassName('team-honor-list-row');
    const numDonations = document.querySelectorAll('.team-honor-list-name');

    const items = [];

    if (numDonations.length > 0) {
      for (const donor of donors) {
        let donorName = donor.querySelector('.team-honor-list-name').innerHTML.trim();
        donorName = donorName.replace('&amp;', `&`);
        const donorAmount = donor.querySelector('.team-honor-list-value').innerHTML.trim();
        if (donorName && donorAmount) {
          items.push({
            title: `${donorName}`,
            subtitle: donorAmount,
          });
        }
      }
      if (items.length > 0) {
        return <TopList {...{ items }} />;
      }
    } else {
      return <p className="has-text-white">No donations yet.</p>;
    }
  };

  const renderTitle = () => {
    const title = screenName.data ? screenName.data : USER_FULL_NAME;
    if (title) {
      return (
        <PageTitle
          title={title}
          donationLink={`${DONATION_LINK}`}
          /* Mockups don't show this edit button but I'm guessing they might want it later so just commenting out. */
          /* editButton={renderEditButton({ text: 'Edit Title', action: () => setEditTitleModalIsOpen(true), className: 'edit-title' })} */
        />
      );
    }
  };

  // eslint-disable-next-line no-shadow
  const renderPersonalActivity = (EVENT_ID, CONS_ID) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [error, setError] = useState(null);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [isLoaded, setIsLoaded] = useState(false);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [items, setItems] = useState({});

    useEffect(() => {
      fetch(
        `https://${BOUNDLESS_MOTION_PREFIX}.boundlessfundraising.com/mobiles/${BOUNDLESS_MOTION_DB}/getMotionActivitySummary?activity_scope=user&event_id=${EVENT_ID}&user_id=${CONS_ID}`,
        {
          method: 'GET',
          headers: headers,
        }
      )
        .then(res => res.json())
        .then(
          result => {
            setIsLoaded(true);
            setItems(result);
          },
          error => {
            setIsLoaded(true);
            setError(error);
          }
        );
    }, []);

    if (error) {
      return <div>Error: {error.message}</div>;
    } else if (!isLoaded) {
      return <div>Loading...</div>;
    } else {
      return (
        <div className="personalActivity">
          <div>
            {items.total > 0 && (
              <>
                <span className="pr-6">
                  Total Miles:{' '}
                  <span className="pl-4">
                    <strong>{items.total}</strong>
                  </span>
                </span>
              </>
            )}
            {items.duration > 0 && (
              <>
                <span className="team-members-number">
                  Total Training Time:{' '}
                  <span className="pl-4">
                    <strong>{items.duration}</strong>
                  </span>
                </span>
              </>
            )}
            {/* <span className="pr-6">Total Miles: <span className="pl-4"><strong>{items.total}</strong></span></span>
            <span>Total Training Time: <span className="pl-4"><strong>{items.duration}</strong></span></span> */}
          </div>
        </div>
      );
    }
  };

  const renderEventNameLink = () => {
    if (TEAM_NAME) {
      return (
        <div className="event-link">
          <a href={EVENT_LINK}>{EVENT_NAME}</a>
          <br />
          Team: <a href={TEAM_URL}>{TEAM_NAME}</a>
        </div>
      );
    } else if (screenName.data) {
      return (
        <div className="event-link">
          <a href={EVENT_LINK}>{EVENT_NAME}</a>
          <br />
          {screenName.data}
        </div>
      );
    }
  };

  const renderProfileImage = () => <ProfileImage src={personalPhoto.data.customUrl} />;

  const renderSocialShare = () => <SocialShare title={'Share'} description={'Help support the kids of St. Jude'} image={'../images/events/socialevents.jpg'} />;

  const renderMobileSocialShareButton = ({ action }) => <button onClick={action}>Share</button>;

  const renderMobileSocialShareModal = () => (
    <MobileSocialShareModal isOpen={mobileSocialShareModalIsOpen} onClose={closeModals} socialShare={renderSocialShare()} />
  );

  const renderEventDetails = () => {
    if (eventInfo.data) {
      const eventDate = luminateExtend.utils.simpleDateFormat(eventInfo.data[0].event_date, 'EEEE, MMMM d, yyyy');
      let eventLocation;
      switch (luminateProperties.frId) {
        case checkFrID(CHICAGO):
          eventLocation = `${eventInfo.data[0].location_name}<br/>${eventInfo.data[0].city}, ${eventInfo.data[0].state} ${eventInfo.data[0].zip}`;
          break;
        case checkFrID(NEWYORK):
          eventLocation = `${eventInfo.data[0].location_name}<br/>${eventInfo.data[0].city}, ${eventInfo.data[0].zip}`;
          break;
        case checkFrID(LONDON):
          eventLocation = `${eventInfo.data[0].city}<br/>${eventInfo.data[0].country}`;
          break;
        case checkFrID(BERLIN):
          eventLocation = `${eventInfo.data[0].city}<br/>${eventInfo.data[0].country}`;
          break;
        default:
          eventLocation = `${eventInfo.data[0].location_name}<br/>${eventInfo.data[0].city}, ${eventInfo.data[0].state} ${eventInfo.data[0].zip}`;
      }
      return (
        <div data-container="event-details">
          <h3 className="has-text-white">Event Details</h3>
          <div className="team-event-detail-container">
            <div className="teams-date-icon">
              <div className="teams-icon">
                <svg id="a" xmlns="http://www.w3.org/2000/svg" width="18.73" height="21.41">
                  <path
                    d="M6.69,1.34v1.34h5.35V1.34c0-.74,.6-1.34,1.34-1.34s1.34,.6,1.34,1.34v1.34h2.01c1.11,0,2.01,.9,2.01,2.01v2.01H0v-2.01c0-1.11,.9-2.01,2.01-2.01h2.01V1.34c0-.74,.6-1.34,1.34-1.34s1.34,.6,1.34,1.34ZM0,8.03H18.73v11.37c0,1.11-.9,2.01-2.01,2.01H2.01c-1.11,0-2.01-.9-2.01-2.01V8.03Zm2.68,4.68c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34c-.37,0-.67,.3-.67,.67v1.34Zm5.35,0c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34c-.37,0-.67,.3-.67,.67v1.34Zm6.02-2.01c-.37,0-.67,.3-.67,.67v1.34c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34ZM2.68,18.06c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34c-.37,0-.67,.3-.67,.67v1.34Zm6.02-2.01c-.37,0-.67,.3-.67,.67v1.34c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34Zm4.68,2.01c0,.37,.3,.67,.67,.67h1.34c.37,0,.67-.3,.67-.67v-1.34c0-.37-.3-.67-.67-.67h-1.34c-.37,0-.67,.3-.67,.67v1.34Z"
                    fill="#ffc32c"
                  />
                </svg>
              </div>
              <div>{eventDate}</div>
            </div>
          </div>
          <div className="team-event-detail-container">
            <div className="teams-location-icon">
              <div className="teams-icon">
                <svg id="a" xmlns="http://www.w3.org/2000/svg" width="15.73" height="20.92">
                  <path
                    d="M6.89,20.45C4.76,17.82,0,11.45,0,7.86,0,3.52,3.52,0,7.86,0s7.86,3.52,7.86,7.86c0,3.58-4.79,9.95-6.89,12.58-.5,.63-1.44,.63-1.94,0h0Zm.97-9.96c1.45,0,2.62-1.18,2.62-2.62s-1.18-2.62-2.62-2.62-2.62,1.18-2.62,2.62,1.18,2.62,2.62,2.62Z"
                    fill="#ffc32c"
                  />
                </svg>
              </div>
              <div>{ReactHtmlParser(eventLocation)}</div>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderChangeGoalModal = () => (
    <EditGoalModal
      isOpen={editGoalModalIsOpen}
      onClose={closeModals}
      goal={convertCentsToDollars(personalProgress.data.goal)}
      handleSubmit={handleUpdateGoal}
      errorMessage={errorMessage}
    />
  );

  const renderChangeTitleModal = () => (
    <EditTitleModal
      isOpen={editTitleModalIsOpen}
      onClose={closeModals}
      title={screenName.data || USER_FULL_NAME}
      handleSubmit={handleUpdateTitle}
      errorMessage={errorMessage}
    />
  );

  const renderChangePhotoModal = () => (
    <EditImageModal
      isOpen={editPhotoModalIsOpen}
      onClose={closeModals}
      image={personalPhoto.data.customUrl}
      isLoading={isLoading}
      handleSubmit={handleUpdatePhoto}
      errorMessage={errorMessage}
    />
  );

  const renderChangeBackgroundPhotoModal = () => {
    if (bgImageData.data && bgImageData.data.images) {
      let imgData;
      switch (EVENT_ID) {
        case checkFrID(CHICAGO):
          imgData = bgImageData.data.images.chicago;
          break;
        case checkFrID(NEWYORK):
          imgData = bgImageData.data.images.newyork;
          break;
        case checkFrID(LONDON):
          imgData = bgImageData.data.images.london;
          break;
        default:
          imgData = bgImageData.data.images.default;
      }
      if (imgData.length > 0) {
        return (
          <EditBackgroundImageModal
            isOpen={editBackgroundPhotoModalIsOpen}
            onClose={closeModals}
            image={selectedBackgroundImage}
            isLoading={isLoading}
            handleSubmit={handleUpdateBackgroundPhoto}
            errorMessage={errorMessage}
            bgImageData={imgData}
          />
        );
      }
    }
  };

  const renderChangeRichTextModal = () => (
    <EditRichTextModal
      isOpen={editRichTextModalIsOpen}
      onClose={closeModals}
      richText={personalPageInfo.data.richText}
      handleSubmit={handleUpdateRichText}
      errorMessage={errorMessage}
    />
  );

  const renderChangeEventModal = () => {
    if (surveyResponses.data.surveyResponses && IS_OWN_PAGE) {
      const eventDetailsResponses = [];
      surveyResponses.data.surveyResponses.forEach((response, i) => {
        if (i < 11) {
          eventDetailsResponses.push({
            key: response.key,
            value: typeof response.responseValue !== 'object' ? response.responseValue : '',
            questionText: response.questionText,
            type: response.questionType,
          });
        }
      });

      return (
        <EditEventDetailsModal
          isOpen={editEventModalIsOpen}
          onClose={closeModals}
          surveyData={eventDetailsResponses}
          handleSubmit={handleUpdateEventDetails}
          isLoading={isLoading}
          errorMessage={errorMessage}
        />
      );
    }
  };

  const renderEditButton = ({ text, action, className }) => {
    if (IS_OWN_PAGE) {
      return (
        <div className={`edit-container ${className ? className : ''}`} data-container="edit-container">
          <button className="button is-link is-small is-narrow" onClick={action}>
            <span className="icon is-small">
              <i className="svg-icon edit-icon is-white"></i>
            </span>
            <span>{text}</span>
          </button>
        </div>
      );
    } else {
      return null;
    }
  };

  const getAchievementLayout = (className, text) => (
    <div className="achievement">
      <div className="achievement-badge">
        <i className={className}></i>
      </div>
      <span>{ReactHtmlParser(text)}</span>
    </div>
  );
  const renderAchievements = () => {
    const achievements = participantAchievements.data.achievements;
    if (achievements) {
      if (
        achievements.facebook_fundraiser === 'FALSE' &&
        achievements.updated_page === 'FALSE' &&
        achievements.donations_1 === 'FALSE' &&
        achievements.donations_5 === 'FALSE' &&
        achievements.is_self_donor === 'FALSE' &&
        achievements.percent_50 === 'FALSE' &&
        achievements.percent_100 === 'FALSE'
      ) {
        return <p className="has-text-white">No achievements to display.</p>;
      } else {
        return (
          <div className="achievements">
            {achievements.facebook_fundraiser === 'TRUE' && getAchievementLayout('achievement-facebook', '<p>Facebook<br>Fundraiser</p>')}
            {achievements.updated_page === 'TRUE' && getAchievementLayout('achievement-storyteller', '<p>St. Jude<br>Storyteller</p>')}
            {achievements.donations_1 === 'TRUE' && getAchievementLayout('achievement-donations-first', '<p>First<br>Donation!</p>')}
            {achievements.donations_5 === 'TRUE' && getAchievementLayout('achievement-donations-five', '<p>5+<br>Donations</p>')}
            {achievements.is_self_donor === 'TRUE' && getAchievementLayout('achievement-kickstart', '<p>Kickstart<br>Donor</p>')}
            {achievements.percent_50 === 'TRUE' && getAchievementLayout('achievement-halfway', '<p>Halfway<br>to Goal</p>')}
            {achievements.percent_100 === 'TRUE' && getAchievementLayout('achievement-goal-met', '<p>Goal Met!</p>')}
          </div>
        );
      }
    } else {
      return <p className="has-text-white">No achievements to display.</p>;
    }
  };

  const renderSidebar = () => (
    <div>
      <div className="dark-gradient-bg">
        {renderEventDetails()}
        <div data-container="achievements">
          <h3 className="has-text-white">{USER_FIRST_NAME}&apos;s Achievements:</h3>
          {renderAchievements()}
        </div>
        <div data-tab-content="donors">
          <h3 className="has-text-white">{USER_FIRST_NAME}&apos;s Donors:</h3>
          <div data-container="donors">{renderTopDonors()}</div>
        </div>
      </div>
    </div>
  );

  const handleUpdateGoal = async values => {
    const response = await updatePersonalGoal({
      eventId: EVENT_ID,
      goal: values.goal * 100,
    });
    if (response.success) {
      personalProgress.reload();
      setEditGoalModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdatePhoto = async blob => {
    setIsLoading(true);
    const response = await updatePersonalPhotos({
      eventId: EVENT_ID,
      photo: blob,
    });
    setIsLoading(false);
    if (response.success) {
      personalPhoto.reload();
      setEditPhotoModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  /* const handleUpdateBackgroundPhoto = async bgImage => {
    setIsLoading(true);
    const responses = [
      {
        type: 'string',
        key: 'question_key_PERSONAL_BACKGROUND',
        value: bgImage,
      },
    ];
    const response = await updateSurveyResponses(EVENT_ID, responses);
    setIsLoading(false);

    if (response[0].success) {
      surveyResponses.reload();
      setEditBackgroundPhotoModalIsOpen(false);
      luminateProperties.personalPage.personalEvent.backgroundImage = bgImage;
    } else {
      setErrorMessage(response[0].message);
    }
  };
 */

  const handleUpdateBackgroundPhoto = async bgImage => {
    setIsLoading(true);
    const responses = surveyResponses.data.surveyResponses;
    for (const obj of responses) {
      if (obj.key === 'PERSONAL_BACKGROUND') {
        obj.responseValue = bgImage;
        break;
      }
    }
    const response = await updateSurveyResponsesForBackgrounds(EVENT_ID, responses);
    setIsLoading(false);

    if (response[0].success) {
      surveyResponses.reload();
      setEditBackgroundPhotoModalIsOpen(false);
      luminateProperties.personalPage.personalEvent.backgroundImage = bgImage;
    } else {
      setErrorMessage(response[0].message);
    }
  };

  const handleUpdateRichText = async values => {
    const response = await updatePersonalPage({
      eventId: EVENT_ID,
      richText: values.richText,
    });
    if (response.success) {
      personalPageInfo.reload();
      setEditRichTextModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleUpdateTitle = async values => {
    const response = await updateScreenName({
      eventId: EVENT_ID,
      screenName: values.title,
    });
    if (response.success) {
      screenName.reload();
      setEditTitleModalIsOpen(false);
    } else {
      setErrorMessage(response.message);
    }
  };

  const handleSetDonationAmount = ({ level_id, amount }) => {
    setDonationLevelId(level_id);
    setDonationAmount(amount);
    if (otherAmount) {
      setIsOtherAmount(true);
    }
  };

  const handleUpdateEventDetails = async (values, surveyData) => {
    setIsLoading(true);
    const responses = [];
    surveyData.map(data => {
      Object.keys(values).map(value => {
        console.log(data.key, values[value]);
        if (data.key === value) {
          responses.push({
            key: `question_key_${data.key}`,
            type: data.type,
            value: values[value],
          });
        }
      });
    });
    const response = await updateSurveyResponses(EVENT_ID, responses);
    setIsLoading(false);

    if (response[0].success) {
      await surveyResponses.reload();
      setEditEventModalIsOpen(false);
    } else {
      setErrorMessage(response[0].message);
    }
  };

  return (
    <div className="heroes-personal">
      {renderChangeGoalModal()}
      {renderChangeTitleModal()}
      {renderChangePhotoModal()}
      {renderChangeBackgroundPhotoModal()}
      {renderChangeRichTextModal()}
      {renderChangeEventModal()}
      {renderMobileSocialShareModal()}

      {renderHeroImage()}
      <section className="section">
        <div className="container">
          <div className="columns">
            {/* left column */}
            <div className="column is-one-quarter-desktop sidebar-section is-hidden-mobile is-hidden-tablet-only">
              <div className="has-text-right" data-container="profile-image">
                {renderEditButton({
                  text: 'Edit Photo',
                  action: () => setEditPhotoModalIsOpen(true),
                })}
                {renderProfileImage()}
              </div>
              {renderSidebar()}
            </div>

            {/* right column */}
            <div className="column is-three-quarter-desktop main-content-section">
              <div className="has-text-right is-hidden-desktop" data-container="profile-image">
                <div className="is-hidden-desktop mobile-social-share-button">
                  {renderMobileSocialShareButton({
                    action: () => setMobileSocialShareModalIsOpen(true),
                  })}
                </div>
                {renderEditButton({
                  text: 'Edit Photo',
                  action: () => setEditPhotoModalIsOpen(true),
                })}
                {renderProfileImage()}
              </div>
              <div className="title-and-progress-container">
                <div data-container="title">
                  <div className="level">
                    <div className="level-left">
                      {renderTitle()}
                      {renderEventNameLink()}
                      {renderPersonalActivity(EVENT_ID, CONS_ID)}
                    </div>
                    <div className="level-right is-hidden-mobile is-hidden-tablet-only">
                      <div data-container="social-share" className="columns is-vcentered">
                        <p className="has-text-weight-bold column is-narrow">Share:</p>
                        {renderSocialShare()}
                      </div>
                    </div>
                  </div>
                </div>
                <div data-container="progress">
                  {renderProgressBar()}
                  {/* {renderDonationPills()} */}
                  {renderDonationButton()}
                </div>
              </div>
              <div data-container="about">
                {renderEditButton({
                  text: 'Edit Story',
                  action: () => setEditRichTextModalIsOpen(true),
                })}
                <br />
                <br />
                {ReactHtmlParser(personalPageInfo.data.richText)}
              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="is-hidden-desktop">{renderSidebar()}</div>
    </div>
  );
};
